import { useState, useEffect, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { rgba } from 'polished'
import { graphql } from 'gatsby'
import {
  Tooltip,
  Container,
  Section,
  Row,
  RowColumn,
} from 'chaoskit/src/components'
import useInterval from 'react-use/lib/useInterval'
import { text, link } from 'chaoskit/src/assets/styles/utility'
import { StylesContainerVariables } from 'chaoskit/src/components/Container'
import { StylesAvatarVariables } from 'chaoskit/src/components/Avatar'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import BaseLayout from '~layouts/BaseLayout'
import Hero, { HeroContent, HeroTitle, HeroTitleSub } from '~partials/Hero'
import { ReactComponent as HeroSVG } from '~media/downtime/hero.svg'
import Data from '~components/Data'
import SocialGroup from '~partials/global/SocialGroup'
import { companies } from '~helpers/downtime-companies'
import logoAmazon from '~media/logos/amazon.svg'
import HelmetSEO from '~partials/global/HelmetSEO'
import Image from '~components/Image'
import TextLink from '~components/TextLink'
import useSiteMetadata from '~hooks/useSiteMetadata'
import { formatPrice } from '~helpers/utility'
import Clock from '~partials/ecommerce-cost-of-downtime/Clock'

const DowntimeSocial = ({ url, ...rest }) => {
  const content =
    'The Cost of Downtime for the Top US Ecommerce Sites from @gremlininc'

  return (
    <SocialGroup
      networks={[
        {
          service: 'twitter',
          url: `https://twitter.com/intent/tweet?text=${content}&url=${url}`,
          title: 'Share on Twitter',
        },
        {
          service: 'linkedin',
          url: `https://www.linkedin.com/shareArticle?title=${content}&url=${url}`,
          title: 'Share on LinkedIn',
        },
        {
          service: 'facebook',
          url: `https://www.facebook.com/sharer.php?u=${url}`,
          title: 'Share on Facebook',
        },
      ]}
      {...rest}
    />
  )
}

DowntimeSocial.propTypes = {
  url: PropTypes.string.isRequired,
}

const GraphLegendItem = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        fontWeight: theme.fontWeight.bold,
        color: theme.fontColor.muted,
      }}
      {...props}
    />
  )
}

const GraphFooterItem = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        ...theme.text.small,
        color: theme.fontColor.muted,
        fontWeight: theme.fontWeight.bold,
      }}
      {...props}
    />
  )
}

const GraphColumn = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        width: '50%',
        paddingLeft: theme.space.base,
        paddingRight: theme.space.base,
        display: 'flex',
        flexDirection: 'column-reverse',
        position: 'relative',

        '&::before': {
          content: "''",
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          background: `linear-gradient(to top, ${rgba(
            theme.color.light.base,
            0
          )}, ${rgba(theme.color.light.base, 0.4)})`,
          pointerEvents: 'none',
        },
      }}
      {...props}
    />
  )
}

const GraphBar = forwardRef(({ amazon, ...rest }, ref) => {
  const theme = useTheme()

  return (
    <div
      ref={ref}
      css={[
        {
          borderRadius: theme.borderRadius.base,
          marginTop: theme.space.xsmall / 2,

          '&:last-child': {
            marginTop: 0,
          },
        },

        amazon && {
          position: 'relative',

          '&::after': {
            content: "''",
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            background: `url(${logoAmazon}) no-repeat center center`,
            backgroundSize: '80% auto',
            width: '100%',
            zIndex: 2,
          },
        },

        !amazon && {
          transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,

          '&:hover, &:focus': {
            transform: `translateX(-${theme.space.xsmall}px)`,
          },
        },
      ]}
      {...rest}
    />
  )
})

GraphBar.propTypes = {
  amazon: PropTypes.bool,
}

const TableRow = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',

        [theme.mq.medium]: {
          gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',

          '&:first-of-type': {
            position: 'sticky',
            top: 0,
            borderTopLeftRadius: theme.borderRadius.base,
            borderTopRightRadius: theme.borderRadius.base,
            background: theme.color.light.base,
            borderBottom: theme.border.base,
            boxShadow: theme.boxShadow.base,
          },
        },

        '&:nth-of-type(even)': {
          background: theme.color.panel.base,
        },

        // Company column
        '> *:first-of-type': {
          background: theme.color.light.base,
          borderRight: theme.border.base,
        },
      }}
      {...props}
    />
  )
}

const TableHeader = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        fontWeight: theme.fontWeight.bold,
        ...theme.text.xsmall,
        color: theme.fontColor.muted,
        textTransform: 'uppercase',
      }}
      {...props}
    />
  )
}

const TableCell = ({ hideOnSmall, ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={[
        {
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          padding: theme.space.base,
        },

        hideOnSmall && {
          [theme.mq.small__max]: {
            display: 'none',
          },
        },
      ]}
      {...rest}
    />
  )
}

TableCell.propTypes = {
  hideOnSmall: PropTypes.bool,
}

const Table = () => {
  const theme = useTheme()

  const [delay, setDelay] = useState(1000)
  const [time, setTime] = useState(0)

  const adjustClock = () => {
    const nextTime = time + 1

    setTime(nextTime)
  }

  useInterval(() => {
    adjustClock()
  }, delay)

  useEffect(() => {
    // Pause after 30 minutes
    if (time >= 1800) {
      setDelay(null)
    }
  }, [time])

  return (
    <aside
      css={{
        maxHeight: 600,
        overflow: 'auto',
        overscrollBehavior: 'contain',
        borderRadius: theme.borderRadius.base,
        background: theme.color.light.base,
        boxShadow: theme.boxShadow.large,
        backfaceVisibility: 'hidden',
        fontSize: theme.fontSize.small,
      }}
    >
      <TableRow>
        <TableCell>
          <TableHeader>Company</TableHeader>
        </TableCell>
        <TableCell hideOnSmall>
          <TableHeader>Estimated Annual Ecommerce Revenue</TableHeader>
        </TableCell>
        <TableCell>
          <TableHeader>Revenue loss per hour</TableHeader>
        </TableCell>
        <TableCell hideOnSmall>
          <TableHeader>Revenue loss per minute</TableHeader>
        </TableCell>
        <TableCell>
          <TableHeader>Revenue loss this session</TableHeader>
        </TableCell>
      </TableRow>
      {companies.map((company) => (
        <TableRow key={company.name}>
          <TableCell
            css={{
              fontWeight: theme.fontWeight.bold,
              color: theme.fontColor.muted,
            }}
          >
            <div
              css={{
                [theme.mq.medium]: {
                  display: 'grid',
                  gridTemplateColumns: `${
                    StylesAvatarVariables(theme).size.base
                  }px 1fr`,
                  gap: theme.space.base,
                  alignItems: 'center',
                },

                [theme.mq.small__max]: {
                  textAlign: 'center',
                },
              }}
            >
              <Image
                css={{
                  borderRadius: theme.borderRadius.base,
                  width: StylesAvatarVariables(theme).size.base,
                  height: StylesAvatarVariables(theme).size.base,
                  objectFit: 'contain',
                }}
                alt={company.name}
                src={company.image}
              />
              <span
                css={{
                  fontSize: theme.fontSize.small,
                  [theme.mq.small__max]: {
                    display: 'none',
                  },
                }}
              >
                {company.name}
              </span>
            </div>
          </TableCell>
          <TableCell hideOnSmall>{formatPrice(company.revenue.year)}</TableCell>
          <TableCell>{formatPrice(company.revenue.second * 3600)}</TableCell>
          <TableCell hideOnSmall>
            {formatPrice(company.revenue.second * 60)}
          </TableCell>
          <TableCell>{formatPrice(company.revenue.second * time)}</TableCell>
        </TableRow>
      ))}
    </aside>
  )
}

const Graph = () => {
  const theme = useTheme()

  const graphOffset = '25'

  const calculateScale = (number) => {
    // Pixel height of graph
    const height = 600
    // Highest amount graph can handle
    const fullValue = 4000
    // Figure out what each $ is equal to in px
    const scale = height / fullValue

    return scale * number
  }

  return (
    <aside>
      <main
        css={{
          position: 'relative',
          height: 600,
          display: 'flex',
          alignItems: 'flex-end',

          [theme.mq.medium]: {
            paddingLeft: `${graphOffset}%`,
          },
        }}
      >
        <div
          css={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            zIndex: -1,

            div: {
              position: 'relative',
              transform: `translateY(-${theme.space.xsmall + 1}px)`,

              span: {
                display: 'block',
                width: `${graphOffset / 1.5}%`,
                textAlign: 'right',

                [theme.mq.xsmall__max]: {
                  display: 'none',
                },
              },

              '&::before': {
                content: "''",
                position: 'absolute',
                background: theme.color.border.base,
                height: 1,
                top: '100%',
                marginTop: theme.space.xsmall,
                left: 0,
                width: '100%',
              },
            },
          }}
        >
          <GraphLegendItem>
            <span>$4,000</span>
          </GraphLegendItem>
          <GraphLegendItem>
            <span>$3,000</span>
          </GraphLegendItem>
          <GraphLegendItem>
            <span>$2,000</span>
          </GraphLegendItem>
          <GraphLegendItem>
            <span>$1,000</span>
          </GraphLegendItem>
          <GraphLegendItem>
            <span>$0</span>
          </GraphLegendItem>
        </div>
        <GraphColumn>
          <Tooltip
            placement="left"
            content={
              <div css={{ padding: theme.space.xsmall }}>
                <div
                  css={{
                    fontWeight: theme.fontWeight.bold,
                    marginBottom: theme.space.xsmall,
                  }}
                >
                  {companies[0].name}
                </div>
                <span css={{ fontSize: theme.fontSize.xsmall }}>
                  ${companies[0].revenue.second}/sec
                </span>
              </div>
            }
          >
            <GraphBar
              amazon
              style={{
                height: calculateScale(companies[0].revenue.second),
                background: companies[0].color,
              }}
            />
          </Tooltip>
        </GraphColumn>
        <GraphColumn>
          {companies.map((company, index) => {
            // Skip over Amazon
            if (index === 0) return

            return (
              <Tooltip
                key={company.name}
                placement="left"
                content={
                  <div css={{ padding: theme.space.xsmall }}>
                    <div
                      css={{
                        fontWeight: theme.fontWeight.bold,
                        marginBottom: theme.space.xsmall,
                      }}
                    >
                      {company.name}
                    </div>
                    <span css={{ fontSize: theme.fontSize.xsmall }}>
                      ${company.revenue.second}/sec
                    </span>
                  </div>
                }
              >
                <GraphBar
                  style={{
                    height: calculateScale(company.revenue.second),
                    background: company.color,
                  }}
                />
              </Tooltip>
            )
          })}
        </GraphColumn>
      </main>
      <footer
        css={{
          marginTop: theme.space.base,
          display: 'flex',
          textAlign: 'center',

          [theme.mq.medium]: {
            paddingLeft: `${graphOffset}%`,
          },

          '> *': {
            width: '50%',
          },
        }}
      >
        <GraphFooterItem>Amazon.com</GraphFooterItem>
        <GraphFooterItem>Traditional Retailers</GraphFooterItem>
      </footer>
    </aside>
  )
}

const Downtime = ({
  data: {
    pageData: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  const { siteUrl } = useSiteMetadata()

  // Workaround for Gatsby's location prop not reliably being available on server-loads
  const currentPageUrl = `${siteUrl}/ecommerce-cost-of-downtime/`

  return (
    <BaseLayout headerProps={{ forceCTAContrast: true }}>
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        image={pageData.socialMediaImage}
      />
      <Hero
        contrast={false}
        css={{
          background: `linear-gradient(to bottom, ${theme.brand.teal} 50%, ${theme.color.primary.base})`,
          overflow: 'hidden',
        }}
      >
        <HeroContent>
          <div
            css={{
              paddingLeft: theme.space.base,
              paddingRight: theme.space.base,
              maxWidth: StylesContainerVariables.base,
              display: 'grid',
              margin: '0 auto',

              [theme.mq.medium]: {
                gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
              },
            }}
          >
            <div
              css={{
                [theme.mq.medium]: {
                  gridRow: 1,
                  gridColumn: '7 / span 6',

                  svg: {
                    maxWidth: 'none',
                    height: 600,
                  },
                },

                [theme.mq.small__max]: {
                  textAlign: 'center',

                  svg: {
                    maxWidth: 400,
                  },
                },
              }}
            >
              <HeroSVG />
            </div>
            <div
              css={{
                position: 'relative',
                zIndex: 1,

                [theme.mq.medium]: {
                  gridRow: '1',
                  gridColumn: '1 / span 6',
                },
              }}
              className="u-contrast"
            >
              <HeroTitle as="h2">
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <HeroTitleSub>
                <Data>{pageData.hero.blurb}</Data>
              </HeroTitleSub>
              <div
                css={{
                  marginTop: theme.space.large,
                  paddingBottom: theme.space.large,
                  borderBottom: `4px solid ${theme.color.light.base}`,
                }}
              >
                <DowntimeSocial
                  css={{ justifyContetn: 'flex-start' }}
                  url={currentPageUrl}
                />
              </div>

              <Clock />
            </div>
          </div>
          <Container>
            <Table />
          </Container>
        </HeroContent>
      </Hero>
      <Section space="xlarge">
        <Container>
          <Row gutter={{ base: 'xlarge' }} css={{ alignItems: 'center' }}>
            <RowColumn
              size={{ large: 6 }}
              css={{
                a: [
                  text.underline,
                  link.reset(theme),
                  {
                    fontWeight: theme.fontWeight.bold,
                  },
                ],
              }}
            >
              <h3
                css={{
                  color: theme.brand.teal,
                  marginBottom: theme.space.large,
                }}
              >
                <Data>{pageData.why.heading}</Data>
              </h3>
              <MDXRenderer>{pageData.why.content}</MDXRenderer>
            </RowColumn>
            <RowColumn size={{ large: 6 }}>
              <div
                css={{
                  ...theme.text.large__fluid,
                  ...text.expanded(theme),
                  color: theme.fontColor.base,
                  marginBottom: theme.space.xlarge,
                  fontWeight: theme.fontWeight.bold,
                }}
              >
                Revenue Per Second
              </div>
              <Graph />
            </RowColumn>
          </Row>
          <div
            id="metrics"
            css={{
              textAlign: 'center',
              marginTop: theme.space.xlarge,
              color: theme.fontColor.muted,
            }}
          >
            Data Source:{' '}
            <TextLink
              css={[
                link.muted(theme),
                text.underline,
                {
                  fontWeight: theme.fontWeight.bold,
                },
              ]}
              href="https://retail-index.emarketer.com/dashboard/c/retailers/5374f2454d4afd2bb4446565/ttm/AllCompanies/All%20Sectors"
            >
              Emarketer retail index
            </TextLink>
          </div>
        </Container>
      </Section>
    </BaseLayout>
  )
}

Downtime.propTypes = {
  data: PropTypes.object.isRequired,
}

export default Downtime

export const pageQuery = graphql`
  query DowntimePageData {
    pageData: file(
      name: { eq: "ecommerce-cost-of-downtime" }
      absolutePath: { regex: "/src/data/" }
    ) {
      childMdx {
        frontmatter {
          title
          socialMediaImage
          description
          hero {
            heading
            blurb
          }
          why {
            heading
            content
          }
        }
      }
    }
  }
`
